import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Smartphone, Mail, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLogin, useMfaChallenge } from '@/api/hooks/useAuth';
import { useCaptchaConfig } from '@/api/hooks/useCaptchaConfig';
import { TurnstileWidget } from '@/components/TurnstileWidget';

// Inline attention-tone alert for sign-in / MFA errors. Reuses the app's established
// amber inline-alert family (SubmitWithEvidenceModal) so a
// failed sign-in is unmissable inside the box — replaces the small top-right toast.
function SignInErrorAlert({ message }: { message: string }) {
  return (
    <div
      className="flex items-start gap-2 rounded-md border border-accent-amber/40 bg-accent-amber/10 px-3 py-2.5"
      role="alert"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent-amber" />
      <p className="text-sm text-accent-amber">{message}</p>
    </div>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // MFA step state
  const [mfaSessionToken, setMfaSessionToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');

  // Reverification confirmation state
  const [reverificationEmail, setReverificationEmail] = useState<string | null>(null);

  // Inline sign-in / MFA error alert state (replaces the top-right toast for these cases)
  const [loginError, setLoginError] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);

  // Help modal state (SSO sign-in help)
  const [showHelpModal, setShowHelpModal] = useState(false);

  // ADMINSEC-3c-2b — uniform Turnstile CAPTCHA (render-when-enabled). The widget is shown
  // whenever CAPTCHA is enabled; the server enforces uniformly (every login challenged).
  const { enabled: captchaEnabled, siteKey: captchaSiteKey } = useCaptchaConfig();
  const showCaptcha = captchaEnabled && !!captchaSiteKey;
  const [turnstileToken, setTurnstileToken] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0); // bump to remount/reset the widget
  function resetCaptcha() {
    setTurnstileToken('');
    setCaptchaNonce((n) => n + 1);
  }

  const login = useLogin(
    (token) => setMfaSessionToken(token),
    (emailAddr) => setReverificationEmail(emailAddr),
    (enrollData) => navigate('/auth/mfa-enrollment', { state: { setupToken: enrollData.setupToken } }),
    (message) => setLoginError(message),
  );
  const mfaChallenge = useMfaChallenge(
    () => setReverificationEmail(email),
    (message) => setMfaError(message),
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password || !subdomain) return;
    setLoginError(null);
    login.mutate(
      { email, password, subdomain, turnstileToken: showCaptcha ? turnstileToken : undefined },
      // A Turnstile token is single-use; a failed attempt consumes it — reset for retry.
      { onError: () => { if (showCaptcha) resetCaptcha(); } },
    );
  }

  function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaSessionToken || totpCode.length !== 6) return;
    setMfaError(null);
    mfaChallenge.mutate({ mfa_session_token: mfaSessionToken, totp_code: totpCode });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg-base px-4">
      {/* Background grid pattern */}
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,159,253,1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,159,253,1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center gap-3">
          <img src="/verigrc-shield-256.png" alt="VeriGRC" className="h-12 w-auto" />
          <div className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-text-primary font-display">VeriGRC</h1>
            <p className="mt-0.5 text-sm text-text-secondary">Unified Cyber GRC Platform</p>
          </div>
        </div>

        <div className="rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
          {/* ── Reverification confirmation step ── */}
          {reverificationEmail ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-cyan/10">
                <Mail className="h-5 w-5 text-accent-cyan" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-text-primary">Check your email to reactivate your account</h2>
                <p className="mt-1.5 text-sm text-text-secondary">
                  We've sent a reactivation link to{' '}
                  <span className="font-medium text-text-primary">{reverificationEmail}</span>.
                  Click the link in the email to restore access.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setReverificationEmail(null)}
                className="text-xs text-text-secondary hover:text-text-primary transition-colors"
              >
                ← Back to sign in
              </button>
            </div>
          ) : /* ── MFA step ── */
          mfaSessionToken ? (
            <>
              <div className="mb-5 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-cyan/10">
                  <Smartphone className="h-5 w-5 text-accent-cyan" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-text-primary">Two-factor authentication</h2>
                  <p className="text-xs text-text-secondary">Enter the code from your authenticator app</p>
                </div>
              </div>

              <form onSubmit={handleMfaSubmit} className="space-y-4" noValidate>
                {mfaError && <SignInErrorAlert message={mfaError} />}
                <div className="space-y-1.5">
                  <Label htmlFor="totp">6-digit code</Label>
                  <Input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => { setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setMfaError(null); }}
                    className="text-center text-xl tracking-[0.4em] font-mono"
                    maxLength={6}
                    autoFocus
                    autoComplete="one-time-code"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={mfaChallenge.isPending || totpCode.length !== 6}
                >
                  {mfaChallenge.isPending ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Verifying…</>
                  ) : (
                    'Verify'
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => { setMfaSessionToken(null); setTotpCode(''); setMfaError(null); if (showCaptcha) resetCaptcha(); }}
                  className="w-full text-center text-xs text-text-secondary hover:text-text-primary transition-colors"
                >
                  ← Back to sign in
                </button>
                <div className="text-center">
                  <Link
                    to={`/forgot-mfa?tenant=${encodeURIComponent(subdomain)}`}
                    className="text-xs text-accent-cyan hover:underline transition-colors"
                  >
                    Lost your authenticator?
                  </Link>
                </div>
              </form>
            </>
          ) : (
            /* ── Credentials step ── */
            <>
              <div className="mb-5">
                <h2 className="text-lg font-semibold text-text-primary">Sign in</h2>
                <p className="mt-0.5 text-sm text-text-secondary">
                  Enter your organization details to continue
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                {loginError && <SignInErrorAlert message={loginError} />}
                <div className="space-y-1.5">
                  <Label htmlFor="subdomain">Organization</Label>
                  <Input
                    id="subdomain"
                    type="text"
                    placeholder="your-company"
                    value={subdomain}
                    onChange={(e) => setSubdomain(e.target.value.toLowerCase().trim())}
                    autoComplete="organization"
                    autoFocus
                    required
                  />
                  <p className="text-xs text-text-secondary">Your VeriGRC subdomain / org slug</p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">Email address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="analyst@company.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setLoginError(null); }}
                    autoComplete="email"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setLoginError(null); }}
                      autoComplete="current-password"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-cyan"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {showCaptcha && (
                  <TurnstileWidget
                    key={captchaNonce}
                    siteKey={captchaSiteKey!}
                    onToken={setTurnstileToken}
                    onExpire={() => setTurnstileToken('')}
                  />
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={login.isPending || !email || !password || !subdomain || (showCaptcha && !turnstileToken)}
                >
                  {login.isPending ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</>
                  ) : (
                    'Sign in'
                  )}
                </Button>

                <div className="text-center">
                  <Link
                    to={subdomain ? `/forgot-password?tenant=${encodeURIComponent(subdomain)}` : '/forgot-password'}
                    className="text-xs text-text-secondary hover:text-text-primary transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
              </form>

              <div className="mt-4 flex items-center gap-2">
                <div className="h-px flex-1 bg-border-subtle" />
                <span className="text-xs text-text-secondary">or</span>
                <div className="h-px flex-1 bg-border-subtle" />
              </div>

              <Button
                type="button"
                variant="outline"
                className="mt-4 w-full"
                onClick={() => {
                  const slug = subdomain.trim();
                  window.location.href = slug
                    ? `/auth/sso/start?slug=${encodeURIComponent(slug)}`
                    : '/auth/sso/start';
                }}
              >
                Sign in with SSO
              </Button>

              <div className="mt-2 text-center">
                <button
                  type="button"
                  onClick={() => setShowHelpModal(true)}
                  className="text-xs text-text-secondary hover:text-text-primary transition-colors"
                >
                  Trouble signing in with SSO?
                </button>
              </div>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-text-secondary">
          Protected by enterprise-grade security ·{' '}
          <span className="text-accent-cyan">verigrc.com</span>
        </p>
      </div>

      {showHelpModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Sign-in help"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowHelpModal(false); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowHelpModal(false); }}
          tabIndex={-1}
        >
          <div className="relative w-full max-w-sm rounded-xl border border-border-subtle bg-bg-surface p-6 shadow-2xl">
            <button
              type="button"
              autoFocus
              onClick={() => setShowHelpModal(false)}
              className="absolute right-4 top-4 text-lg leading-none text-text-secondary hover:text-text-primary"
              aria-label="Close sign-in help"
            >
              ×
            </button>
            <h2 className="mb-4 text-base font-semibold text-text-primary">Trouble signing in with SSO?</h2>
            <p className="text-sm text-text-secondary">
              Contact your organization administrator for assistance.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
